import React from 'react';
import { Alert, Button, Modal } from 'antd';

import IntlMessages from '@src/util/IntlMessages';

import type { MarkingConfirmCopy } from '@src/models/interfaces/markingPortal.interface';
import styles from '../styles.module.less';

export type MarkingPortalConfirmModalPhase = 'confirm' | 'success';

interface MarkingPortalConfirmModalProps {
  open: boolean;
  phase: MarkingPortalConfirmModalPhase;
  copy: MarkingConfirmCopy;
  displayName: string;
  markingAtDisplay: string;
  isSubmitting: boolean;
  resetSecondsRemaining: number;
  earlyWarningId?: string | null;
  onConfirm: () => void;
  onBackToStart: () => void;
  onClose: () => void;
}

export const MarkingPortalConfirmModal: React.FC<MarkingPortalConfirmModalProps> = ({
  open,
  phase,
  copy,
  displayName,
  markingAtDisplay,
  isSubmitting,
  resetSecondsRemaining,
  earlyWarningId,
  onConfirm,
  onBackToStart,
  onClose,
}) => {
  const isSuccess = phase === 'success';

  return (
    <Modal
      open={open}
      footer={null}
      closable={!isSuccess}
      maskClosable={!isSuccess}
      centered
      width={480}
      className={styles.confirmModal}
      onCancel={onClose}
    >
      <div className={styles.confirmModalContent}>
        <h2 className={styles.confirmModalTitle}>
          <IntlMessages id={isSuccess ? copy.successTitleId : copy.titleId} />
        </h2>

        {isSuccess ? (
          <div className={styles.confirmModalSuccessIcon} aria-hidden>
            <span className="material-symbols-outlined">check</span>
          </div>
        ) : (
          <>
            <div className={styles.confirmModalSummary}>
              <div className={styles.confirmModalSummaryRow}>
                <span className={styles.confirmModalSummaryLabel}>
                  <IntlMessages id="markingPortal.marking.field.user" />
                </span>
                <span className={styles.confirmModalSummaryValue}>{displayName}</span>
              </div>
              <div className={styles.confirmModalSummaryRow}>
                <span className={styles.confirmModalSummaryLabel}>
                  <IntlMessages id="markingPortal.marking.confirm.field.marking" />
                </span>
                <span className={styles.confirmModalSummaryValue}>{markingAtDisplay}</span>
              </div>
            </div>

            {earlyWarningId && (
              <Alert
                type="warning"
                showIcon
                className={styles.confirmModalWarning}
                message={<IntlMessages id={earlyWarningId} />}
              />
            )}
          </>
        )}

        <Button
          type="primary"
          block
          size="large"
          loading={!isSuccess && isSubmitting}
          className={styles.confirmModalPrimaryButton}
          onClick={isSuccess ? onBackToStart : onConfirm}
        >
          <IntlMessages
            id={isSuccess ? 'markingPortal.marking.confirm.backToStart' : copy.submitId}
          />
        </Button>

        <p className={styles.confirmModalFooter}>
          <IntlMessages
            id={
              isSuccess
                ? 'markingPortal.marking.confirm.success.resetHint'
                : 'markingPortal.marking.confirm.identityDisclaimer'
            }
            values={isSuccess ? { seconds: resetSecondsRemaining } : undefined}
          />
        </p>
      </div>
    </Modal>
  );
};
